import { useEffect, useId, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

interface BaseProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly className?: string;
}

/** Recuerda qué elemento tenía el foco al abrir y se lo devuelve al cerrar (si sigue en la página). */
function useFocusReturn(): void {
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (opener?.isConnected === true) opener.focus();
      else document.getElementById('main')?.focus();
    };
  }, []);
}

/**
 * Diálogo modal: `<dialog>` con `showModal()`, que atenúa la ventana, atrapa el foco y se cierra con Esc.
 * Se monta solo mientras está abierto.
 */
export function ModalDialog({ title, onClose, children, className }: BaseProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useFocusReturn();

  useEffect(() => {
    const d = ref.current;
    if (d === null) return;
    if (!d.open) d.showModal();
    // Esc dispara «cancel»: el cierre se delega en el propietario del estado.
    const onCancel = (e: Event): void => {
      e.preventDefault();
      onClose();
    };
    d.addEventListener('cancel', onCancel);
    return () => d.removeEventListener('cancel', onCancel);
  }, [onClose]);

  return (
    <dialog ref={ref} className={`dialog dialog--modal ${className ?? ''}`} aria-labelledby={titleId}>
      <div className="dialog__title">
        <h2 id={titleId}>{title}</h2>
        <button type="button" aria-label="Cerrar" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="dialog__body">{children}</div>
    </dialog>
  );
}

/**
 * Diálogo flotante no bloqueante (`show()`): no atenúa ni bloquea la ventana, así que la previsualización
 * se actualiza mientras se edita. Se arrastra por la barra de título y se cierra con Esc.
 */
export function FloatingDialog({ title, onClose, children, className }: BaseProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const drag = useRef<{ dx: number; dy: number } | undefined>(undefined);
  const titleId = useId();
  useFocusReturn();

  useEffect(() => {
    const d = ref.current;
    if (d === null) return;
    if (!d.open) d.show();
    d.querySelector<HTMLElement>('.dialog__body input, .dialog__body select, .dialog__body textarea')?.focus();
  }, []);

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>): void => {
    const d = ref.current;
    if (d === null || (e.target as HTMLElement).closest('button') !== null) return;
    const r = d.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>): void => {
    const d = ref.current;
    const at = drag.current;
    if (d === null || at === undefined) return;
    // Se mantiene dentro de la ventana para que la barra de título no se pierda.
    const x = Math.min(Math.max(e.clientX - at.dx, 0), Math.max(window.innerWidth - 80, 0));
    const y = Math.min(Math.max(e.clientY - at.dy, 0), Math.max(window.innerHeight - 30, 0));
    d.style.left = `${x}px`;
    d.style.top = `${y}px`;
    d.style.right = 'auto';
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLElement>): void => {
    drag.current = undefined;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  return (
    <dialog
      ref={ref}
      className={`dialog dialog--floating ${className ?? ''}`}
      aria-labelledby={titleId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="dialog__title dialog__title--drag" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <h2 id={titleId}>{title}</h2>
        <button type="button" aria-label="Cerrar" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="dialog__body">{children}</div>
    </dialog>
  );
}
